import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface AdderConfig {
  name: string;
  cost: number;
  variable: boolean;
  min?: number;
  max?: number;
  defaultCost?: number;
}
export const ADDERS: AdderConfig[] = [{
  name: 'Climatech Elite Plus',
  cost: 0,
  variable: false
}, {
  name: 'Grey/Bronze Tint',
  cost: 75,
  variable: false
}, {
  name: 'SCB Tint',
  cost: 185,
  variable: false
}, {
  name: 'Climatech Elite Extreme Plus',
  cost: 300,
  variable: false
}, {
  name: 'Stucco Cut Back',
  cost: 1000,
  variable: false
}, {
  name: 'Mulling Windows',
  cost: 500,
  variable: true,
  min: 500,
  max: 1000,
  defaultCost: 500
}, {
  name: 'Sound Package',
  cost: 350,
  variable: false
}];
export interface AdderSelection {
  [adderName: string]: {
    checked: boolean;
    amount: number;
  };
}
export const DOOR_ADDERS: AdderConfig[] = [{
  name: 'Small Dog Door',
  cost: 2000,
  variable: false
}, {
  name: 'Medium Dog Door',
  cost: 2000,
  variable: false
}, {
  name: 'Large Dog Door',
  cost: 2000,
  variable: false
}, {
  name: 'XL Dog Door',
  cost: 3000,
  variable: false
}];
export function createDefaultAdderSelection(config: AdderConfig[] = ADDERS): AdderSelection {
  const selection: AdderSelection = {};
  config.forEach(adder => {
    selection[adder.name] = {
      checked: false,
      amount: adder.variable ? adder.defaultCost ?? adder.cost : adder.cost
    };
  });
  return selection;
}
export function calculateAdderTotal(adders: AdderSelection): number {
  return Object.values(adders).reduce((sum, a) => {
    if (a.checked) return sum + a.amount;
    return sum;
  }, 0);
}
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface AdderSelectorProps {
  adders: AdderSelection;
  onChange: (adders: AdderSelection) => void;
  adderConfig?: AdderConfig[];
}
export function AdderSelector({
  adders,
  onChange,
  adderConfig = ADDERS
}: AdderSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedCount = Object.values(adders).filter(a => a.checked).length;
  const totalCost = calculateAdderTotal(adders);
  function handleToggle(adderName: string) {
    const updated = {
      ...adders
    };
    updated[adderName] = {
      ...updated[adderName],
      checked: !updated[adderName].checked
    };
    onChange(updated);
  }
  function handleAmountChange(adderName: string, value: number) {
    const config = adderConfig.find(a => a.name === adderName);
    if (!config || !config.variable) return;
    const clamped = Math.min(Math.max(value, config.min ?? 0), config.max ?? Infinity);
    const updated = {
      ...adders
    };
    updated[adderName] = {
      ...updated[adderName],
      amount: clamped
    };
    onChange(updated);
  }
  const selectedAdderNames = Object.entries(adders).filter(([, v]) => v.checked).map(([k]) => k);
  return <div className="relative" data-id="element-3">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors text-gray-700 font-medium" data-id="element-4">
        <span data-id="element-5">
          {selectedCount === 0 ? 'Select Adders' : `${selectedCount} adder${selectedCount > 1 ? 's' : ''}`}
        </span>
        {isOpen ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-6" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-7" />}
      </button>

      {selectedAdderNames.length > 0 && !isOpen && <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-8">
          {selectedAdderNames.map(name => <span key={name} className="inline-block px-1.5 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded" data-id="element-9">
              {name}
            </span>)}
        </div>}

      {isOpen && <div className="absolute z-20 top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg" data-id="element-10">
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between" data-id="element-11">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-12">
              Add-Ons
            </span>
            {totalCost > 0 && <span className="text-xs font-semibold text-emerald-700" data-id="element-13">
                {formatCurrency(totalCost)}
              </span>}
          </div>
          <div className="py-1" data-id="element-14">
            {adderConfig.map(adder => {
          const sel = adders[adder.name];
          return <div key={adder.name} className="px-3 py-2 hover:bg-gray-50" data-id="element-15">
                  <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-16">
                    <input type="checkbox" checked={sel?.checked ?? false} onChange={() => handleToggle(adder.name)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0" data-id="element-17" />
                    <div className="flex-1 min-w-0" data-id="element-18">
                      <div className="flex items-center justify-between" data-id="element-19">
                        <span className="text-sm text-gray-800" data-id="element-20">
                          {adder.name}
                        </span>
                        <span className="text-sm text-gray-500 ml-2 tabular-nums" data-id="element-21">
                          {adder.variable ? `$${adder.min}–${adder.max}` : adder.cost === 0 ? 'Included' : formatCurrency(adder.cost)}
                        </span>
                      </div>
                      {adder.variable && sel?.checked && <div className="mt-1.5 flex items-center gap-2" data-id="element-22">
                          <span className="text-xs text-gray-500" data-id="element-23">Amount:</span>
                          <div className="relative" data-id="element-24">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400" data-id="element-25">
                              $
                            </span>
                            <input type="number" value={sel.amount} min={adder.min} max={adder.max} onChange={e => handleAmountChange(adder.name, Number(e.target.value))} className="w-24 pl-5 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums" data-id="element-26" />
                          </div>
                          <span className="text-[11px] text-gray-400" data-id="element-27">
                            ({adder.min}–{adder.max})
                          </span>
                        </div>}
                    </div>
                  </label>
                </div>;
        })}
          </div>
          <div className="px-3 py-2 border-t border-gray-100" data-id="element-28">
            <button type="button" onClick={() => setIsOpen(false)} className="w-full text-center text-sm font-medium text-emerald-700 hover:text-emerald-800 py-1" data-id="element-29">
              Done
            </button>
          </div>
        </div>}
    </div>;
}